import { Button } from "@fanduel/formation-react-components";
import { ArrowsChevronRight } from "@fanduel/formation-react-icons";
import { useState } from "react";
import Link from "@/components/link/Link";
import { generateH2HMatchupUrl } from "@/utils/headToHead";
import type { Game } from "../types";

interface GameActionButtonProps {
  game: Game;
}

export interface GameActionButtonConfig {
  type: "h2h" | "sportsbook";
  url: string;
  label: string;
  isExternal?: boolean;
}

/**
 * Gets the configuration for rendering a game action button
 * Returns button config for either H2H link, sportsbook link, or null
 */
const getGameActionButtonConfig = (
  game: Game
): GameActionButtonConfig | null => {
  const gameId = game.numberFireId?.toString();
  const awayTeamShortName = game.awayTeam?.shortName;
  const homeTeamShortName = game.homeTeam?.shortName;
  const cannotConstructH2HUrl =
    !gameId || !awayTeamShortName || !homeTeamShortName;

  if (cannotConstructH2HUrl) {
    if (game.odds?.url) {
      return {
        type: "sportsbook",
        url: game.odds?.url,
        label: "Explore the Event in Sportsbook",
        isExternal: true,
      };
    }

    return null;
  }

  const h2hUrl = generateH2HMatchupUrl(
    gameId,
    awayTeamShortName,
    homeTeamShortName
  );

  return {
    type: "h2h",
    url: h2hUrl,
    label: "View Game Page",
    isExternal: false,
  };
};

const GameActionButton = ({ game }: GameActionButtonProps) => {
  const buttonConfig = getGameActionButtonConfig(game);

  const [isLoading, setLoading] = useState(false);

  if (!buttonConfig) {
    return null;
  }

  const buttonElement = (
    <Button
      label={buttonConfig.label}
      size="small"
      variant="tertiary"
      loading={isLoading}
      fill={true}
      iconRight={<ArrowsChevronRight />}
      onClick={() => setLoading(true)}
    />
  );

  if (buttonConfig.isExternal) {
    return (
      <a href={buttonConfig.url} target="_blank" rel="noreferrer">
        {buttonElement}
      </a>
    );
  }

  return (
    <Link next href={buttonConfig.url}>
      {buttonElement}
    </Link>
  );
};

export default GameActionButton;
